/**
 * Social, footer and legal links shared by the Sign In page, the System
 * Screens and the Footer.
 *
 * The footer URLs are the ones the current ARC / Events / University
 * footers already use; the YouTube channel and the address/phone links come
 * from the Claude Design footer spec.
 */

export interface AllianceLink {
  label: string;
  href: string;
}

export interface AllianceSocialLink extends AllianceLink {
  /** Brand hover color on light and dark surfaces */
  hover: { light: string; dark: string };
  /** 24×24 filled icon path (footer only) */
  iconPath?: string;
}

const INSTAGRAM = 'https://www.instagram.com/nationalagentsalliance';
const YOUTUBE = 'https://www.youtube.com/@TheAllianceYT';

/**
 * Sign In page and System Screens: only three platforms, to drive traffic
 * to a few accounts instead of listing every one.
 */
export const SIGN_IN_SOCIAL_LINKS: AllianceSocialLink[] = [
  {
    label: 'Instagram',
    href: INSTAGRAM,
    hover: { light: '#C13584', dark: '#F56FA8' },
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@nationalagentsalliance',
    hover: { light: '#000000', dark: '#25F4EE' },
  },
  {
    label: 'YouTube',
    href: YOUTUBE,
    hover: { light: '#E00000', dark: '#FF4E45' },
  },
];

/** Footer: all five current platforms, as 36px round icon buttons */
export const FOOTER_SOCIAL_LINKS: AllianceSocialLink[] = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/groups/330822977953654',
    hover: { light: '#4599FF', dark: '#4599FF' },
    iconPath:
      'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2m13 2h-2.5A3.5 3.5 0 0 0 12 8.5V11h-2v3h2v7h3v-7h3v-3h-3V9a1 1 0 0 1 1-1h2V5z',
  },
  {
    label: 'Instagram',
    href: INSTAGRAM,
    hover: { light: '#F56FA8', dark: '#F56FA8' },
    iconPath:
      'M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10m0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  },
  {
    label: 'X (Twitter)',
    href: 'https://twitter.com/NaaLeadsTheWay',
    hover: { light: '#ffffff', dark: '#ffffff' },
    iconPath:
      'M17.75 3h3.07l-6.71 7.67L22 21h-6.17l-4.84-6.32L5.46 21H2.39l7.18-8.2L2 3h6.33l4.37 5.78zm-1.08 16.18h1.7L7.4 4.73H5.58z',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/national-agents-alliance-corporate',
    hover: { light: '#5EA8F5', dark: '#5EA8F5' },
    iconPath:
      'M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z',
  },
  {
    label: 'YouTube',
    href: YOUTUBE,
    hover: { light: '#FF4E45', dark: '#FF4E45' },
    iconPath:
      'M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22 1.1.28 1.9.07.8.1 1.49.1 2.09L22 12c0 2.19-.16 3.8-.44 4.83-.25.9-.83 1.48-1.73 1.73-.47.13-1.33.22-2.65.28-1.3.07-2.49.1-3.59.1L12 19c-4.19 0-6.8-.16-7.83-.44-.9-.25-1.48-.83-1.73-1.73-.13-.47-.22-1.1-.28-1.9-.07-.8-.1-1.49-.1-2.09L2 12c0-2.19.16-3.8.44-4.83.25-.9.83-1.48 1.73-1.73.47-.13 1.33-.22 2.65-.28 1.3-.07 2.49-.1 3.59-.1L12 5c4.19 0 6.8.16 7.83.44.9.25 1.48.83 1.73 1.73z',
  },
];

const ADDRESS_MAP =
  'https://maps.google.com/?q=1214+Turrentine+Street+Burlington+NC+27215';

/** Footer columns, in order. Connect is open by default on phones */
export const FOOTER_COLUMNS: { title: string; links: AllianceLink[] }[] = [
  {
    title: 'Connect',
    links: [
      { label: 'The Alliance', href: 'https://www.naaleads.com/' },
      { label: '1214 Turrentine Street', href: ADDRESS_MAP },
      { label: 'Burlington, NC 27215', href: ADDRESS_MAP },
      { label: '336.227.3319', href: 'tel:+13362273319' },
    ],
  },
  {
    title: 'Tours',
    links: [
      { label: 'Quality Leads', href: 'https://leadperformanceteam.com/' },
      {
        label: 'Professional Training',
        href: 'https://www.naaleads.com/tour/training',
      },
      {
        label: 'Turnkey System',
        href: 'https://www.naaleads.com/tour/turnkey-system',
      },
      {
        label: 'Reliable Products',
        href: 'https://www.naaleads.com/tour/products',
      },
      {
        label: 'Compensation',
        href: 'https://www.naaleads.com/tour/compensation',
      },
    ],
  },
  {
    title: 'About',
    links: [
      { label: 'About The Alliance', href: 'https://www.naaleads.com/about' },
      { label: 'Our Story', href: 'https://www.naaleads.com/about/our-story' },
      {
        label: 'Leadership',
        href: 'https://www.naaleads.com/about/our-leadership',
      },
      {
        label: 'Impact',
        href: 'https://www.naaleads.com/about/charitable-partners',
      },
    ],
  },
  {
    title: 'Other',
    links: [
      {
        label: 'Corporate Jobs',
        href: 'https://www.naaleads.com/corporate-jobs',
      },
      { label: 'Contact Us', href: 'https://www.naaleads.com/contact' },
      { label: 'Sitemap', href: 'https://www.naaleads.com/sitemap' },
    ],
  },
];

/** The legal row under the footer columns */
export const LEGAL_LINKS: AllianceLink[] = [
  { label: 'Privacy Policy', href: 'https://www.integrity.com/privacy-policy' },
  {
    label: 'NAA Terms of Service',
    href: 'https://a7d9021b860b18c4222a-d761a6fa6590560fc9f9e4d318d2556e.ssl.cf1.rackcdn.com/contracting/documents/alliance-terms-of-service.pdf',
  },
  {
    label: 'Do Not Sell My Information',
    href: 'https://submit-irm.trustarc.com/services/validation/d14968e7-edec-4831-b5f2-c494567844f6',
  },
];

/** "Copyright © {year} NAA. All Rights Reserved." (final design mock) */
export const copyrightText = () =>
  `Copyright © ${new Date().getFullYear()} NAA. All Rights Reserved.`;
